import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@common/components/icons/SearchIcon';
import CloseIcon from '@common/components/icons/CloseIcon';
import CheckmarkIcon from '@common/components/icons/CheckmarkIcon';
import * as styles from './MultiSelectList.module.css';

/**
 * MultiSelectList Component
 *
 * A list-based multi-select component with search, checkboxes, and selected chips.
 * Matches the Figma design for the Schedule Report page.
 *
 * @param {Array} items - Array of items to display in the list
 * @param {Array} selectedIds - Array of selected item IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {string} searchPlaceholder - Placeholder text for search input
 * @param {string} labelKey - Key to use for item label (default: 'name')
 * @param {string} descriptionKey - Key to use for item description (default: 'description')
 * @param {string} idKey - Key to use for item ID (default: 'id')
 * @param {string} typeKey - Key to use for item type (for filtering, default: 'type')
 * @param {Function} renderBadge - Optional function to render a badge for each item
 * @param {boolean} showChips - Whether to show selected items as chips (default: true)
 * @param {boolean} showChipSubtitle - Whether to show subtitle in chips (default: false)
 * @param {string} chipSubtitleKey - Key to use for chip subtitle (default: 'type')
 * @param {string} emptyMessage - Message to show when no items available
 * @param {string} noResultsMessage - Message to show when search returns no results
 * @param {boolean} loading - Whether the list is loading
 * @param {string} id - ID for the search input (for accessibility)
 * @param {string} label - Label text for the field
 * @param {Array} filterOptions - Array of filter options for type filtering
 * @param {Function} onFilterChange - Callback when filter changes (receives active filter types)
 * @param {Array} activeFilters - Array of active filter types
 * @param {string} filterMode - Filter display mode: 'checkbox' (default) or 'radio'
 * @param {string} bothLabel - Optional label for "Both" option in radio mode (if not provided, "Both" option is hidden)
 */
function MultiSelectList({
  items,
  selectedIds,
  onSelectionChange,
  searchPlaceholder,
  labelKey,
  descriptionKey,
  idKey,
  typeKey,
  renderBadge,
  showChips,
  showChipSubtitle,
  chipSubtitleKey,
  emptyMessage,
  noResultsMessage,
  loading,
  id,
  label,
  filterOptions,
  onFilterChange,
  activeFilters,
  filterMode, // 'checkbox' (default) or 'radio'
  bothLabel, // Label for "Both" option in radio mode
}) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter items by search term and active filters
  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }

    let result = items;

    // Apply type filter if filter options are provided
    if (filterOptions && filterOptions.length > 0 && activeFilters && activeFilters.length > 0) {
      // If not all filters are active, filter by active types
      // When all filters are active, show all items
      if (activeFilters.length < filterOptions.length) {
        result = result.filter((item) => {
          const itemType = item[typeKey];
          return itemType && activeFilters.includes(itemType);
        });
      }
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase().trim();
      if (search) {
        result = result.filter((item) => {
          const labelValue = (item[labelKey] || '').toLowerCase();
          const descValue = (item[descriptionKey] || '').toLowerCase();
          return labelValue.includes(search) || descValue.includes(search);
        });
      }
    }

    return result;
  }, [items, searchTerm, labelKey, descriptionKey, filterOptions, activeFilters, typeKey]);

  // Get selected item objects
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.includes(item[idKey])),
    [items, selectedIds, idKey],
  );

  // Handle item toggle
  const handleToggle = useCallback(
    (itemId) => {
      const newSelection = selectedIds.includes(itemId)
        ? selectedIds.filter((id2) => id2 !== itemId)
        : [...selectedIds, itemId];
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange],
  );

  // Handle chip removal
  const handleRemoveChip = useCallback(
    (itemId) => {
      onSelectionChange(selectedIds.filter((id2) => id2 !== itemId));
    },
    [selectedIds, onSelectionChange],
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Get all filter values for "Both" option
  const allFilterValues = useMemo(
    () => (filterOptions || []).map((option) => option.value),
    [filterOptions],
  );

  // Check if "Both" is selected (all filters active)
  const isBothSelected = useMemo(() => {
    if (allFilterValues.length === 0) return false;
    return allFilterValues.every((value) => activeFilters?.includes(value));
  }, [allFilterValues, activeFilters]);

  // Get currently selected radio value (for radio mode)
  const selectedRadioValue = useMemo(() => {
    // If bothLabel is provided and all filters are selected, show "both" as selected
    if (bothLabel && isBothSelected) return 'both';
    // If only one filter is active, show that filter as selected
    if (activeFilters?.length === 1) return activeFilters[0];
    // Default: if bothLabel exists, default to 'both', otherwise to first filter
    return bothLabel ? 'both' : (activeFilters?.[0] || allFilterValues[0]);
  }, [isBothSelected, activeFilters, bothLabel, allFilterValues]);

  // Handle radio button change (for radio mode)
  const handleRadioChange = useCallback(
    (value) => {
      if (!onFilterChange) return;
      
      if (value === 'both') {
        // Select all filters
        onFilterChange(allFilterValues);
      } else {
        // Select only the chosen filter
        onFilterChange([value]);
      }
    },
    [onFilterChange, allFilterValues],
  );

  // Handle checkbox filter toggle (for checkbox mode - original behavior)
  const handleCheckboxFilterToggle = useCallback(
    (filterValue) => {
      if (!onFilterChange) return;

      const currentFilters = activeFilters || [];
      const newFilters = currentFilters.includes(filterValue)
        ? currentFilters.filter((f) => f !== filterValue)
        : [...currentFilters, filterValue];

      // Ensure at least one filter is always active
      if (newFilters.length === 0) {
        return;
      }

      onFilterChange(newFilters);
    },
    [activeFilters, onFilterChange],
  );

  // Get chip subtitle text
  const getChipSubtitle = useCallback(
    (item) => {
      if (!showChipSubtitle) return null;
      const typeValue = item[chipSubtitleKey];
      // Map type values to display text
      if (typeValue === 'contact') return t('scheduledReports.recipientTypes.individualContacts');
      if (typeValue === 'contactGroup') return t('scheduledReports.recipientTypes.contactGroups');
      return typeValue;
    },
    [showChipSubtitle, chipSubtitleKey, t],
  );

  const defaultEmptyMessage = t('common.noItemsAvailable');
  const defaultNoResultsMessage = t('common.noItemsFound');

  return (
    <div className={styles.container}>
      {/* Label */}
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}

      {/* Filter Options */}
      {filterOptions && filterOptions.length > 0 && (
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>{t('common.show')}:</span>
          <div className={styles.filterOptions}>
            {filterMode === 'radio' ? (
              <>
                {/* Radio mode: optional "Both" + individual options as radio buttons */}
                {bothLabel && (
                  <label className={styles.radioLabel} htmlFor={`${id}-filter-both`}>
                    <input
                      type="radio"
                      id={`${id}-filter-both`}
                      name={`${id}-filter-type`}
                      value="both"
                      checked={selectedRadioValue === 'both'}
                      onChange={() => handleRadioChange('both')}
                      className={styles.radioInput}
                    />
                    <span>{bothLabel}</span>
                  </label>
                )}
                {filterOptions.map((option) => (
                  <label key={option.value} className={styles.radioLabel} htmlFor={`${id}-filter-${option.value}`}>
                    <input
                      type="radio"
                      id={`${id}-filter-${option.value}`}
                      name={`${id}-filter-type`}
                      value={option.value}
                      checked={selectedRadioValue === option.value}
                      onChange={() => handleRadioChange(option.value)}
                      className={styles.radioInput}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </>
            ) : (
              <>
                {/* Checkbox mode: original behavior */}
                {filterOptions.map((option) => {
                  const isChecked = activeFilters?.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={styles.filterOption}
                      onClick={() => handleCheckboxFilterToggle(option.value)}
                    >
                      <span className={styles.checkboxContainer}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          className={styles.customCheckbox}
                          tabIndex={-1}
                          aria-hidden="true"
                        />
                        {isChecked && (
                          <span className={styles.checkboxIconContainer}>
                            <CheckmarkIcon />
                          </span>
                        )}
                      </span>
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className={styles.searchWrapper}>
        <SearchIcon />
        <input
          id={id}
          type="text"
          className={styles.searchInput}
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* Selection List */}
      <div className={styles.list}>
        {loading && <div className={styles.loading}>{t('common.loading')}</div>}

        {!loading && items.length === 0 && <div className={styles.empty}>{emptyMessage || defaultEmptyMessage}</div>}

        {!loading && items.length > 0 && filteredItems.length === 0 && (
          <div className={styles.empty}>{noResultsMessage || defaultNoResultsMessage}</div>
        )}

        {!loading &&
          filteredItems.map((item) => {
            const itemId = item[idKey];
            const isSelected = selectedIds.includes(itemId);
            const itemLabel = item[labelKey] || '';
            const itemDescription = item[descriptionKey] || '';

            return (
              <button
                key={itemId}
                type="button"
                className={`${styles.item} ${isSelected ? styles.selected : ''}`}
                onClick={() => handleToggle(itemId)}
              >
                <span className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className={styles.customCheckbox}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  {isSelected && (
                    <span className={styles.checkboxIconContainer}>
                      <CheckmarkIcon />
                    </span>
                  )}
                </span>
                <div className={styles.itemContent}>
                  <span className={styles.itemLabel}>{itemLabel}</span>
                  {itemDescription && <span className={styles.itemDescription}>{itemDescription}</span>}
                </div>
                {renderBadge && renderBadge(item)}
              </button>
            );
          })}
      </div>

      {/* Selected Chips */}
      {showChips && selectedItems.length > 0 && (
        <div className={styles.chips}>
          {selectedItems.map((item) => {
            const subtitle = getChipSubtitle(item);
            return (
              <div key={item[idKey]} className={`${styles.chip} ${showChipSubtitle ? styles.chipWithSubtitle : ''}`}>
                <div className={styles.chipContent}>
                  <span className={styles.chipLabel}>{item[labelKey]}</span>
                  {subtitle && <span className={styles.chipSubtitle}>{subtitle}</span>}
                </div>
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => handleRemoveChip(item[idKey])}
                  aria-label={`Remove ${item[labelKey]}`}
                >
                  <CloseIcon />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

MultiSelectList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  labelKey: PropTypes.string,
  descriptionKey: PropTypes.string,
  idKey: PropTypes.string,
  typeKey: PropTypes.string,
  renderBadge: PropTypes.func,
  showChips: PropTypes.bool,
  showChipSubtitle: PropTypes.bool,
  chipSubtitleKey: PropTypes.string,
  emptyMessage: PropTypes.string,
  noResultsMessage: PropTypes.string,
  loading: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  onFilterChange: PropTypes.func,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  filterMode: PropTypes.oneOf(['checkbox', 'radio']),
  bothLabel: PropTypes.string,
};

MultiSelectList.defaultProps = {
  searchPlaceholder: 'Search...',
  labelKey: 'name',
  descriptionKey: 'description',
  idKey: 'id',
  typeKey: 'type',
  renderBadge: null,
  showChips: true,
  showChipSubtitle: false,
  chipSubtitleKey: 'type',
  emptyMessage: null,
  noResultsMessage: null,
  loading: false,
  id: undefined,
  label: null,
  filterOptions: null,
  onFilterChange: null,
  activeFilters: [],
  filterMode: 'checkbox',
  bothLabel: null,
};

export default MultiSelectList;
